import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useSalesApi } from '@/client/api/sales/sales';

// Live per-source scrape data + bring-over leads, served by ff-api's Sales floor
// (/api/admin/v2/sales/sources and /leads), which proxies ff-prospect's OLAP. Goes
// through the same admin-session-gated path as the rest of the Sales suite — no
// direct ff-prospect proxy, no token in this app. A newly onboarded scraper shows
// up automatically.

export interface ProspectSourceRow {
  source: string;
  listings: number;
  live: number;
  sellers: number;
  sellers_with_phone: number;
  for_sale: number;
  for_rent: number;
  inventory_ghs: number;
  cross_posted: number;
  verticals: number;
  last_scraped: string | null;
}

export interface ProspectSourcesOverview {
  country: string;
  sources: ProspectSourceRow[];
  totals: {
    listings: number;
    live: number;
    sellers: number;
    sellers_with_phone: number;
    for_sale: number;
    for_rent: number;
    inventory_ghs: number;
    cross_posted: number;
  };
}

export interface ProspectLead {
  name: string | null;
  phone: string | null;
  source: string;
  listings: number;
  verticals: number;
  paid: boolean | null;
  premium: boolean | null;
  kind: string | null;
  last_seen: string | null;
}

export interface ProspectLeads {
  country: string;
  source: string | null;
  vertical: string | null;
  prospects: ProspectLead[];
}

export const prospectKeys = {
  all: ['prospect-admin'] as const,
  sources: (country: string) => [...prospectKeys.all, 'sources-overview', country] as const,
  leads: (country: string, source?: string, vertical?: string) =>
    [...prospectKeys.all, 'top-prospects', country, source ?? '', vertical ?? ''] as const,
};

export const useProspectSourcesQuery = (country = 'ghana', enabled = true) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: prospectKeys.sources(country),
    queryFn: () =>
      api<ProspectSourcesOverview>(`/sources?country=${encodeURIComponent(country)}`),
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useProspectLeadsQuery = (
  country = 'ghana',
  source?: string,
  vertical?: string,
  enabled = true,
) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: prospectKeys.leads(country, source, vertical),
    queryFn: () => {
      const qs = new URLSearchParams({ country });
      if (source) qs.set('source', source);
      if (vertical) qs.set('vertical', vertical);
      return api<ProspectLeads>(`/leads?${qs.toString()}`);
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
